// @generated-imports
import { applyFiltersAndRender, clearFilters, escapeHTML, findGameById, showToast, truncate, updateContinueButton, updateFavCountBadge } from './app.js';
import { BACKUP_EXPORT_EXCLUDE, idbCountAll, idbOpen, isArrayBufferLike, listEmulatorDatabases, renderBackupSummary } from './backup.js';
import { CONFIG, dom, state } from './core.js';
import { LOCAL_AMBIGUOUS_EXTENSIONS, LOCAL_EXTENSION_CORE_MAP } from './dragdrop.js';
import { formatBytes } from './format.js';
import { isLocalRomId, localRomAsGame } from './library.js';
import { FOCUSABLE_SELECTOR, initLiteMode } from './lite.js';
import { refreshSaveIndex } from './platform.js';
import { renderRecentGames } from './prefs.js';
import { MAIN_PROFILE_ID, profileName, splitProfileIdentity } from './profiles.js';
import { describeSlot } from './states.js';
// @generated-imports
/* ===========================================================================
   GESTÃO DE DADOS NO PAINEL DE SAVES (v1.4.0 → v1.5.0)
   ---------------------------------------------------------------------------
   O painel lista os bancos do emulador (saves, BIOS, cache) com itens
   apagáveis, as configurações do portal com limpeza por item e uma zona de
   risco. Toda exclusão pede um SEGUNDO clique de confirmação no próprio
   botão — navegável por mouse, teclado e d-pad.

   v1.5.0 — camada de NOMES AMIGÁVEIS: bancos e itens deixam de aparecer
   como `EmulatorJS-Cache` / URLs gigantes e passam a ser "Save states",
   "Top Gear — save state", "Núcleo snes9x", com agrupamento por tipo e
   subtotais de tamanho. O dado técnico segue em tooltip/<code>. Leitura
   de tamanhos em streaming (sem reter valores), inventário compartilhado
   com o resumo, re-render por bloco e token contra renders concorrentes.
   ======================================================================== */

/** Confirmação em dois toques: o 1º clique arma o botão ("Confirmar?", 4s);
 *  só o 2º dentro da janela retorna true. Dialogs nativos (confirm()) não
 *  são navegáveis por controle — este padrão é. */
function armButtonConfirm(button) {
  if (button.dataset.armed === '1') {
    window.clearTimeout(Number(button.dataset.armedTimer || 0));
    button.dataset.armed = '';
    button.classList.remove('is-armed');
    button.textContent = button.dataset.armedLabel || button.textContent;
    return true;
  }
  button.dataset.armed = '1';
  button.dataset.armedLabel = button.textContent;
  button.textContent = 'Confirmar?';
  button.classList.add('is-armed');
  const timer = window.setTimeout(() => {
    button.dataset.armed = '';
    button.classList.remove('is-armed');
    button.textContent = button.dataset.armedLabel || button.textContent;
  }, 4000);
  button.dataset.armedTimer = String(timer);
  return false;
}

/** Estimativa de tamanho de um valor do IndexedDB (exibição; não precisa
 *  ser exata — buffers/blobs dominam e esses são medidos de verdade). */
function estimateValueSize(value, depth = 0) {
  if (value === null || value === undefined) return 0;
  if (isArrayBufferLike(value)) return value.byteLength;
  if (ArrayBuffer.isView(value)) return value.byteLength;
  if (typeof value === 'string') return value.length * 2;
  if (typeof value === 'number' || typeof value === 'boolean') return 8;
  if (typeof value === 'object' && typeof value.size === 'number'
    && typeof value.arrayBuffer === 'function') return value.size; // Blob/File
  if (depth >= 3) return 64;
  if (Array.isArray(value)) {
    return value.reduce((sum, item) => sum + estimateValueSize(item, depth + 1), 16);
  }
  if (typeof value === 'object') {
    let sum = 16;
    for (const key of Object.keys(value)) {
      sum += key.length * 2 + estimateValueSize(value[key], depth + 1);
    }
    return sum;
  }
  return 8;
}

/** Chave de IDB legível (pode ser string, número, array, Date…). */
function formatIdbKey(key) {
  try {
    if (typeof key === 'string') return key;
    if (typeof key === 'number') return String(key);
    if (key instanceof Date) return key.toISOString();
    if (Array.isArray(key)) return key.map(formatIdbKey).join(' / ');
    return JSON.stringify(key);
  } catch (_) {
    return String(key);
  }
}

/* --- Nomes amigáveis (v1.5.0) --- */

/** Bancos do EmulatorJS → rótulo humano + o que significa apagar. */
const EMU_DB_LABELS = [
  { test: /^emutab-states$/i, icon: '⏪', label: 'Estados do EmuTab (retomar)', hint: 'autosave ao sair e slots manuais — irrecuperável se apagar' },
  { test: /^emutab-library$/i, icon: '📼', label: 'Biblioteca (metadados)', hint: 'nomes, sistemas e capas das suas ROMs locais' },
  { test: /states?/i, icon: '💾', label: 'Save states', hint: 'seu progresso nos jogos — irrecuperável se apagar' },
  { test: /cache/i, icon: '📦', label: 'Cache de núcleos e ROMs', hint: 're-baixável — seguro apagar para liberar espaço' },
  { test: /bios/i, icon: '🔧', label: 'BIOS', hint: 'arquivos de sistema de alguns consoles' },
  { test: /cores?$/i, icon: '⚙️', label: 'Núcleos instalados', hint: 'emuladores baixados — voltam sozinhos' },
  { test: /roms?/i, icon: '🎮', label: 'ROMs guardadas pelo emulador', hint: 're-baixáveis' },
];

function describeEmuDb(name) {
  const match = EMU_DB_LABELS.find((entry) => entry.test.test(String(name || '')));
  return match
    ? { icon: match.icon, label: match.label, hint: match.hint }
    : { icon: '🗄️', label: String(name), hint: 'banco do emulador' };
}

const EMU_GROUP_ORDER = ['Save states', 'Memória do cartucho', 'ROMs', 'Núcleos', 'BIOS', 'Outros'];
const EMU_STATE_SUFFIX = /\.state(\d*)$/i;
const EMU_SRAM_SUFFIX = /\.(srm|sav|brm|eep|fla|mcr|sram|rtc)$/i;
const EMU_CORE_FILE = /\.(wasm|js|data|mem)(\.gz)?$/i;

function stripExtension(name) {
  return String(name || '').replace(/\.[^.]+$/, '');
}

/** Último segmento de uma URL/caminho, decodificado (sem query/hash). */
function basenameOf(key) {
  let text = formatIdbKey(key);
  try { text = decodeURIComponent(text); } catch (_) { /* mantém cru */ }
  text = text.split(/[?#]/)[0];
  const parts = text.split('/');
  return parts[parts.length - 1] || text;
}

/** Índice "nome de arquivo / título" → título do jogo, do catálogo e da
 *  biblioteca local. Indexa o basename, o basename sem extensão e o próprio
 *  título (o player passa o título como EJS_gameName — o EmulatorJS pode
 *  usar qualquer um deles como base do nome do save). */
function buildRomTitleIndex() {
  const index = new Map();
  const add = (name, title) => {
    if (!name || !title) return;
    const key = String(name).toLowerCase();
    if (!index.has(key)) index.set(key, title);
  };
  for (const game of state.roms) {
    const base = basenameOf(game.romUrl);
    add(base, game.title);
    add(stripExtension(base), game.title);
    add(game.title, game.title);
  }
  for (const entry of state.localRoms.values()) {
    add(entry.fileName, entry.title);
    add(stripExtension(entry.fileName), entry.title);
    add(entry.title, entry.title);
  }
  return index;
}

/** Traduz a chave técnica de um item em {title, kind, group}. Cai para o
 *  basename quando nada casa — nunca pior do que mostrar a chave crua. */
function describeEmuEntry(dbName, key, romIndex) {
  const technical = formatIdbKey(key);
  if (dbName === 'emutab-states') {
    const colon = technical.lastIndexOf(':');
    const hasSlot = colon > technical.indexOf('|');
    const identity = hasSlot ? technical.slice(0, colon) : technical;
    const slot = hasSlot ? technical.slice(colon + 1) : '';
    const { profile, rest } = splitProfileIdentity(identity);
    const gameId = rest.split('|')[0];
    const game = findGameById(gameId) || (state.localRoms.get(gameId) ? localRomAsGame(state.localRoms.get(gameId)) : null);
    const owner = profile !== MAIN_PROFILE_ID ? ` · perfil ${profileName(profile)}` : '';
    const kind = slot ? `Estado — ${describeSlot(slot)}${owner}` : `Índice de estados${owner}`;
    return { title: game ? game.title : gameId, kind, group: 'Save states', technical, matched: Boolean(game) };
  }
  if (dbName === 'emutab-library') {
    const meta = state.localRoms.get(technical);
    return { title: meta ? meta.title : technical, kind: 'Metadados de ROM local', group: 'Outros', technical, matched: Boolean(meta) };
  }
  const base = basenameOf(key);
  const lookup = (name) => romIndex.get(String(name || '').toLowerCase()) || null;

  const stateMatch = EMU_STATE_SUFFIX.exec(base);
  if (stateMatch) {
    const stem = base.replace(EMU_STATE_SUFFIX, '');
    const game = lookup(stem) || lookup(stripExtension(stem));
    const slot = stateMatch[1];
    return {
      title: game || stem,
      kind: slot ? `Save state (slot ${slot})` : 'Save state',
      group: 'Save states',
      technical,
      matched: Boolean(game),
    };
  }
  if (EMU_SRAM_SUFFIX.test(base)) {
    const stem = base.replace(EMU_SRAM_SUFFIX, '');
    const game = lookup(stem) || lookup(stripExtension(stem));
    return { title: game || stem, kind: 'Memória do cartucho (SRAM)', group: 'Memória do cartucho', technical, matched: Boolean(game) };
  }
  if (/bios/i.test(dbName)) {
    return { title: base, kind: 'BIOS', group: 'BIOS', technical, matched: false };
  }
  if (EMU_CORE_FILE.test(base)) {
    const core = base.replace(EMU_CORE_FILE, '').replace(/-(wasm|thread|legacy)/gi, '').replace(/[-_.]+$/, '');
    return { title: `Núcleo ${core || base}`, kind: 'Arquivo do emulador', group: 'Núcleos', technical, matched: false };
  }
  const game = lookup(base) || lookup(stripExtension(base));
  if (game) {
    return { title: game, kind: 'ROM em cache', group: 'ROMs', technical, matched: true };
  }
  const ext = (base.split('.').pop() || '').toLowerCase();
  if (ext && (LOCAL_EXTENSION_CORE_MAP[ext] || LOCAL_AMBIGUOUS_EXTENSIONS[ext])) {
    return { title: base, kind: 'ROM', group: 'ROMs', technical, matched: false };
  }
  return { title: base || technical, kind: 'Arquivo', group: 'Outros', technical, matched: false };
}

/* --- Inventário compartilhado (resumo + gestão) --- */

async function computeEmuInventory() {
  let databases = [];
  try {
    databases = await listEmulatorDatabases();
  } catch (_) { /* lista vazia */ }
  const inventory = [];
  for (const meta of databases) {
    let total = null;
    try {
      // eslint-disable-next-line no-await-in-loop
      total = await idbCountAll(meta.name);
    } catch (_) { /* inacessível */ }
    inventory.push({ name: meta.name, total });
  }
  return inventory;
}

/** Cacheia a PROMESSA: resumo e gestão, disparados juntos ao abrir o painel,
 *  compartilham a mesma passada em vez de abrir cada banco duas vezes. */
function getEmuInventory(force = false) {
  if (force || !state.emuInventoryPromise) state.emuInventoryPromise = computeEmuInventory();
  return state.emuInventoryPromise;
}

function invalidateEmuInventory() {
  state.emuInventoryPromise = null;
}

/* --- Leitura de tamanhos em streaming --- */

/** Percorre a store somando tamanhos SEM reter os valores — um Cache com
 *  centenas de MB de núcleos não pode ir inteiro para a memória numa TV. */
function idbScanStoreSizes(db, storeName) {
  return new Promise((resolve, reject) => {
    const found = [];
    const tx = db.transaction(storeName, 'readonly');
    const cursor = tx.objectStore(storeName).openCursor();
    cursor.onsuccess = () => {
      const c = cursor.result;
      if (!c) { resolve(found); return; }
      found.push({ key: c.key, size: estimateValueSize(c.value) });
      c.continue();
    };
    cursor.onerror = () => reject(cursor.error);
  });
}

async function listDatabaseEntries(dbName) {
  const db = await idbOpen(dbName);
  const entries = [];
  for (const storeName of [...db.objectStoreNames]) {
    // eslint-disable-next-line no-await-in-loop
    const scanned = await idbScanStoreSizes(db, storeName);
    for (const item of scanned) {
      entries.push({ db: dbName, store: storeName, key: item.key, size: item.size });
    }
  }
  db.close();
  return entries;
}

async function deleteDatabaseEntry(dbName, storeName, key) {
  const db = await idbOpen(dbName);
  await new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    tx.objectStore(storeName).delete(key);
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
  db.close();
}

/** Apaga um banco inteiro. Conexões abertas (jogo rodando, outra aba)
 *  bloqueiam o delete — devolvemos false após 1,5s para a UI avisar. */
function deleteWholeDatabase(name) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok) => {
      if (!settled) { settled = true; resolve(ok); }
    };
    let request;
    try {
      request = indexedDB.deleteDatabase(name);
    } catch (_) {
      finish(false);
      return;
    }
    request.onsuccess = () => finish(true);
    request.onerror = () => finish(false);
    request.onblocked = () => window.setTimeout(() => finish(false), 1500);
  });
}

/** Devolve o foco a um ponto estável depois que o botão clicado sumiu no
 *  re-render (senão o foco cai no body, dentro do trap mas "no nada"). */
function focusFirstEnabled(container, preferredSelector) {
  if (!container) return;
  let target = preferredSelector ? container.querySelector(preferredSelector) : null;
  if (!target || target.disabled) {
    target = [...container.querySelectorAll(FOCUSABLE_SELECTOR)]
      .find((el) => !el.disabled && el.offsetParent !== null) || null;
  }
  if (!target) target = dom.backupClose;
  if (target) { try { target.focus(); } catch (_) {} }
}

/* --- Painel: dados do emulador --- */

function pluralItems(count) {
  return `${count} ite${count === 1 ? 'm' : 'ns'}`;
}

function emuDbHeaderHtml(name, total, expanded, totalSize) {
  const info = describeEmuDb(name);
  const isCache = BACKUP_EXPORT_EXCLUDE.test(name);
  const countText = total === null ? 'inacessível' : pluralItems(total);
  const sizeText = expanded && totalSize !== null ? ` • ${formatBytes(totalSize)}` : '';
  const canExpand = total !== null && total > 0;
  const toggleLabel = expanded ? 'Ocultar' : (canExpand ? `Ver ${total} ite${total === 1 ? 'm' : 'ns'}` : 'Vazio');
  return `<div class="backup-row data-row data-db-head">
    <span class="data-info" title="Banco: ${escapeHTML(name)}"><span aria-hidden="true">${info.icon}</span> <strong>${escapeHTML(info.label)}</strong>
      <small>${countText}${sizeText} • ${escapeHTML(info.hint)}${isCache ? ' • fora do backup' : ''} <code>${escapeHTML(name)}</code></small></span>
    <span class="data-actions">
      <button class="mini-btn" type="button" data-toggle-db="${escapeHTML(name)}" aria-expanded="${expanded ? 'true' : 'false'}"${canExpand ? '' : ' disabled'}>${toggleLabel}</button>
      <button class="mini-btn mini-btn-danger" type="button" data-del-db="${escapeHTML(name)}" aria-label="Apagar ${escapeHTML(info.label)}"${total === null ? ' disabled' : ''}>Apagar banco</button>
    </span>
  </div>`;
}

async function emuDbEntriesHtml(name, romIndex) {
  let entries = [];
  try {
    entries = await listDatabaseEntries(name);
  } catch (_) { /* vazio abaixo */ }
  if (!entries.length) {
    return { html: '<div class="backup-row data-row data-sub"><span class="data-info"><small>vazio</small></span></div>', totalSize: 0 };
  }

  const described = entries.map((entry) => ({ entry, info: describeEmuEntry(name, entry.key, romIndex) }));
  const groups = new Map();
  described.forEach((item) => {
    const list = groups.get(item.info.group) || [];
    list.push(item);
    groups.set(item.info.group, list);
  });
  const orderedGroups = [...groups.keys()]
    .sort((a, b) => EMU_GROUP_ORDER.indexOf(a) - EMU_GROUP_ORDER.indexOf(b));

  let totalSize = 0;
  const parts = [];
  for (const groupName of orderedGroups) {
    const items = groups.get(groupName);
    // Saves ordenam por jogo (é assim que a pessoa procura); cache/núcleos
    // por tamanho — responde "o que está ocupando meu espaço?".
    const byTitle = groupName === 'Save states' || groupName === 'Memória do cartucho';
    items.sort((a, b) => (byTitle
      ? a.info.title.localeCompare(b.info.title)
      : b.entry.size - a.entry.size));
    const subtotal = items.reduce((sum, item) => sum + item.entry.size, 0);
    totalSize += subtotal;
    if (orderedGroups.length > 1 || items.length > 1) {
      parts.push(`<div class="backup-row data-row data-sub data-group"><span class="data-info"><small>${escapeHTML(groupName)} — ${pluralItems(items.length)} • ${formatBytes(subtotal)}</small></span></div>`);
    }
    for (const { entry, info } of items) {
      state.emuEntrySeq += 1;
      const id = state.emuEntrySeq;
      state.emuDataEntries.set(id, entry);
      const showTechnical = info.technical !== info.title;
      parts.push(`<div class="backup-row data-row data-sub">
        <span class="data-info" title="${escapeHTML(info.technical)}">${escapeHTML(truncate(info.title, 38))}
          <small>${escapeHTML(info.kind)} • ${formatBytes(entry.size)}${showTechnical ? ` • <code>${escapeHTML(truncate(info.technical, 40))}</code>` : ''}</small></span>
        <button class="mini-btn mini-btn-danger" type="button" data-del-entry="${id}" aria-label="Apagar ${escapeHTML(info.title)} (${escapeHTML(info.kind)})">Apagar</button>
      </div>`);
    }
  }
  return { html: parts.join(''), totalSize };
}

async function emuDbBlockHtml(name, total, romIndex) {
  const expanded = state.emuExpandedDbs.has(name);
  let entriesHtml = '';
  let totalSize = null;
  if (expanded && total) {
    const result = await emuDbEntriesHtml(name, romIndex);
    entriesHtml = result.html;
    totalSize = result.totalSize;
  }
  return emuDbHeaderHtml(name, total, expanded, totalSize) + entriesHtml;
}

function findEmuDbBlock(name) {
  if (!dom.emuDataList) return null;
  return [...dom.emuDataList.querySelectorAll('[data-emu-db]')]
    .find((el) => el.dataset.emuDb === name) || null;
}

async function renderEmuDataList() {
  if (!dom.emuDataList) return;
  state.emuRenderToken += 1;
  const token = state.emuRenderToken;
  dom.emuDataList.innerHTML = '<div class="backup-row">Carregando…</div>';

  const inventory = await getEmuInventory();
  if (token !== state.emuRenderToken) return; // render mais novo assumiu
  if (!inventory.length) {
    dom.emuDataList.innerHTML = '<div class="backup-row">Nada guardado pelo emulador ainda — saves e cache aparecem aqui depois de jogar.</div>';
    return;
  }

  const romIndex = buildRomTitleIndex();
  const blocks = [];
  for (const item of inventory) {
    // eslint-disable-next-line no-await-in-loop
    const html = await emuDbBlockHtml(item.name, item.total, romIndex);
    blocks.push(`<div class="emu-db" data-emu-db="${escapeHTML(item.name)}">${html}</div>`);
  }
  // Render mais novo assumiu enquanto este rodava: descarta o DOM obsoleto.
  // Os ids dos itens são únicos e monotônicos (emuEntrySeq), então nenhum
  // botão aponta para o registro de outro render — o Map só é zerado ao
  // abrir o painel.
  if (token !== state.emuRenderToken) return;
  dom.emuDataList.innerHTML = blocks.join('');
}

/** Re-renderiza só o bloco de um banco (toggle/exclusão de item) — sem o
 *  "Carregando…" piscar na lista inteira. */
async function refreshEmuDbBlock(name, options = {}) {
  const block = findEmuDbBlock(name);
  if (!block) { await renderEmuDataList(); return; }
  if (options.loading) {
    block.insertAdjacentHTML('beforeend', '<div class="backup-row data-row data-sub"><span class="data-info"><small>Carregando itens…</small></span></div>');
  }
  const inventory = await getEmuInventory();
  const item = inventory.find((entry) => entry.name === name);
  if (!item) { await renderEmuDataList(); return; }
  block.innerHTML = await emuDbBlockHtml(name, item.total, buildRomTitleIndex());
  if (options.focus) focusFirstEnabled(block, '[data-toggle-db]');
}

async function handleEmuDataClick(event) {
  const toggle = event.target.closest('[data-toggle-db]');
  if (toggle) {
    if (toggle.disabled) return;
    const name = toggle.dataset.toggleDb;
    if (state.emuExpandedDbs.has(name)) state.emuExpandedDbs.delete(name);
    else state.emuExpandedDbs.add(name);
    await refreshEmuDbBlock(name, { loading: true, focus: true });
    return;
  }

  const delDb = event.target.closest('[data-del-db]');
  if (delDb) {
    if (delDb.disabled || !armButtonConfirm(delDb)) return;
    const name = delDb.dataset.delDb;
    if (state.currentIframe) {
      showToast('Desligue o jogo antes de apagar um banco inteiro — o emulador mantém a conexão aberta e o navegador bloqueia a exclusão.');
      return;
    }
    const info = describeEmuDb(name);
    const ok = await deleteWholeDatabase(name);
    showToast(ok
      ? `${info.label} apagado.`
      : `Não deu para apagar ${info.label} — feche outras abas do EmuTab e tente de novo.`);
    state.emuExpandedDbs.delete(name);
    invalidateEmuInventory();
    refreshSaveIndex().then(() => applyFiltersAndRender()); // selos 💾 acompanham
    await renderEmuDataList();
    renderBackupSummary();
    focusFirstEnabled(dom.emuDataList);
    return;
  }

  const delEntry = event.target.closest('[data-del-entry]');
  if (delEntry) {
    if (!armButtonConfirm(delEntry)) return;
    const entry = state.emuDataEntries.get(Number(delEntry.dataset.delEntry));
    if (!entry) return;
    try {
      // Apagar um ITEM funciona mesmo com o jogo aberto (transação normal,
      // sem mudança de versão) — só o delete do banco inteiro exige jogo off.
      await deleteDatabaseEntry(entry.db, entry.store, entry.key);
      showToast('Item apagado.');
    } catch (_) {
      showToast('Não deu para apagar este item.');
    }
    invalidateEmuInventory();
    refreshSaveIndex().then(() => applyFiltersAndRender());
    await refreshEmuDbBlock(entry.db, { focus: true });
    renderBackupSummary();
  }
}

/* --- Painel: configurações do portal --- */

const FILTER_DEFAULTS = { genre: 'all', system: 'all', sort: 'featured', favoritesOnly: false, view: 'cover' };

function filtersAreCustomized() {
  return Boolean(state.query)
    || state.genre !== FILTER_DEFAULTS.genre
    || state.system !== FILTER_DEFAULTS.system
    || state.sort !== FILTER_DEFAULTS.sort
    || Boolean(state.favoritesOnly) !== FILTER_DEFAULTS.favoritesOnly
    || state.view !== FILTER_DEFAULTS.view;
}

function lastGameLabel() {
  const lastGameId = localStorage.getItem(CONFIG.localStorageLastGameKey);
  if (!lastGameId) return null;
  const game = isLocalRomId(lastGameId) ? state.localRoms.get(lastGameId) : findGameById(lastGameId);
  return game ? game.title : lastGameId;
}

function configRowsSpec() {
  const controlKeys = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.indexOf('emutab:controls:') === 0) controlKeys.push(key);
    }
  } catch (_) { /* noop */ }

  const liteValue = (() => {
    try { return localStorage.getItem(CONFIG.localStorageLiteKey); } catch (_) { return null; }
  })();
  const lastGame = lastGameLabel();
  const customized = filtersAreCustomized();

  return [
    {
      id: 'favorites', keys: [CONFIG.localStorageFavoritesKey], label: 'Favoritos',
      detail: `${state.favorites.size} jogo(s)`, empty: state.favorites.size === 0,
      after: () => {
        state.favorites = new Set();
        updateFavCountBadge(); // o badge do toolbar não era atualizado por este caminho
        applyFiltersAndRender();
      },
    },
    {
      id: 'recents', keys: [CONFIG.localStorageRecentKey], label: 'Recentes',
      detail: `${state.recentGames.length} atalho(s)`, empty: state.recentGames.length === 0,
      after: () => { state.recentGames = []; renderRecentGames(); },
    },
    {
      id: 'last-game', keys: [CONFIG.localStorageLastGameKey], label: 'Último jogo (Continuar)',
      detail: lastGame ? `Continuar: ${truncate(lastGame, 28)}` : 'vazio',
      empty: !lastGame,
      after: () => updateContinueButton(),
    },
    {
      id: 'play-counts', keys: [CONFIG.localStoragePlayCountsKey], label: 'Contagens de partidas',
      detail: `${Object.keys(state.playCounts || {}).length} jogo(s)`,
      empty: Object.keys(state.playCounts || {}).length === 0,
      after: () => { state.playCounts = {}; applyFiltersAndRender(); },
    },
    {
      id: 'filters', keys: [CONFIG.localStorageFiltersKey], label: 'Filtros e ordenação',
      // Compara VALORES com os padrões (a chave existe quase sempre, porque
      // persistFilters grava a cada interação — "personalizados" mentia).
      detail: customized ? 'personalizados' : 'padrão',
      empty: !customized,
      // clearFilters reseta estado + selects + re-renderiza; sem focar a
      // busca, que jogaria o foco para trás do painel aberto.
      after: () => clearFilters({ focusSearch: false }),
    },
    {
      id: 'lite', keys: [CONFIG.localStorageLiteKey], label: 'Modo leve (preferência)',
      detail: liteValue === '1' ? 'ligado' : liteValue === '0' ? 'desligado' : 'automático',
      empty: liteValue !== '1' && liteValue !== '0',
      // Sem a chave, initLiteMode volta ao modo automático (re-detecta).
      after: () => initLiteMode(),
    },
    {
      id: 'controls', keys: controlKeys, label: 'Controles por sistema',
      detail: `${controlKeys.length} sistema(s) configurado(s)`, empty: controlKeys.length === 0,
      // Sem hook: o próximo jogo semeia os padrões de novo sozinho.
      after: null,
    },
  ];
}

function renderConfigList() {
  if (!dom.configList) return;
  const rows = configRowsSpec().map((row) => `<div class="backup-row data-row">
    <span class="data-info">${row.label} <small>${escapeHTML(row.detail)}</small></span>
    <button class="mini-btn mini-btn-danger" type="button" data-clear-config="${row.id}" aria-label="Limpar ${escapeHTML(row.label)}"${row.empty ? ' disabled' : ''}>Limpar</button>
  </div>`);
  dom.configList.innerHTML = rows.join('');
}

function handleConfigClick(event) {
  const button = event.target.closest('[data-clear-config]');
  if (!button || button.disabled) return;
  if (!armButtonConfirm(button)) return;
  const id = button.dataset.clearConfig;
  const spec = configRowsSpec().find((row) => row.id === id);
  if (!spec) return;
  try {
    spec.keys.forEach((key) => localStorage.removeItem(key));
  } catch (_) { /* noop */ }
  if (typeof spec.after === 'function') spec.after();
  showToast(`${spec.label}: limpo.`);
  renderConfigList();
  renderBackupSummary();
  focusFirstEnabled(dom.configList, `[data-clear-config="${id}"]`);
}

/* --- Zona de risco --- */

async function wipeEmulatorData() {
  if (state.currentIframe) {
    showToast('Desligue o jogo antes de apagar os dados do emulador — com o jogo aberto os bancos ficam travados.');
    return;
  }
  let databases = [];
  try {
    databases = await listEmulatorDatabases();
  } catch (_) { /* noop */ }
  if (!databases.length) {
    showToast('Nada para apagar — o emulador ainda não guardou dados.');
    return;
  }
  let failed = 0;
  for (const meta of databases) {
    // eslint-disable-next-line no-await-in-loop
    const ok = await deleteWholeDatabase(meta.name);
    if (!ok) failed += 1;
  }
  showToast(failed
    ? `${failed} banco(s) não puderam ser apagados — feche outras abas do EmuTab e tente de novo.`
    : 'Todos os dados do emulador foram apagados.');
  state.emuExpandedDbs = new Set();
  invalidateEmuInventory();
  refreshSaveIndex().then(() => applyFiltersAndRender());
  await renderEmuDataList();
  renderBackupSummary();
  focusFirstEnabled(dom.emuDataList);
}

async function clearAppCache() {
  try {
    const keys = await caches.keys();
    await Promise.all(
      keys.filter((key) => key.indexOf('emutab-shell-') === 0).map((key) => caches.delete(key))
    );
    // O precache só roda no `install` do service worker: limpar o cache e
    // deixar o worker vivo degradava o offline até a PRÓXIMA versão (o
    // shell voltava aos poucos). Desregistrar + recarregar reinstala o
    // worker e refaz o precache completo.
    if (navigator.serviceWorker && typeof navigator.serviceWorker.getRegistrations === 'function') {
      // Só o registro do EmuTab: www.naves.com.br hospeda outros apps na
      // mesma origem e getRegistrations() devolve TODOS eles.
      const ours = new URL('./', window.location.href).href;
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations
        .filter((registration) => {
          const worker = registration.active || registration.waiting || registration.installing;
          const script = worker ? worker.scriptURL : '';
          return registration.scope === ours || (script && script.indexOf(ours) === 0);
        })
        .map((registration) => registration.unregister()));
    }
    showToast('Cache do aplicativo limpo. Recarregue para reinstalar o modo offline completo.', {
      action: { label: 'Recarregar agora', handler: () => window.location.reload() },
    });
  } catch (_) {
    showToast('Não consegui limpar o cache do aplicativo neste navegador.');
  }
  renderBackupSummary();
}

// @generated-exports
export { EMU_CORE_FILE, EMU_DB_LABELS, EMU_GROUP_ORDER, EMU_SRAM_SUFFIX, EMU_STATE_SUFFIX, FILTER_DEFAULTS, armButtonConfirm, basenameOf, buildRomTitleIndex, clearAppCache, computeEmuInventory, configRowsSpec, deleteDatabaseEntry, deleteWholeDatabase, describeEmuDb, describeEmuEntry, emuDbBlockHtml, emuDbEntriesHtml, emuDbHeaderHtml, estimateValueSize, filtersAreCustomized, findEmuDbBlock, focusFirstEnabled, formatIdbKey, getEmuInventory, handleConfigClick, handleEmuDataClick, idbScanStoreSizes, invalidateEmuInventory, lastGameLabel, listDatabaseEntries, pluralItems, refreshEmuDbBlock, renderConfigList, renderEmuDataList, stripExtension, wipeEmulatorData };
// @generated-exports
